import styled, { css } from 'styled-components';

import { TransientProps } from '@common/types';

type HasCurrentUserProps = TransientProps<{
  currentUser: boolean;
}>;

type ContainerProps = HasCurrentUserProps &
  TransientProps<{
    removedMessage: boolean;
  }>;

export const ReplyButtonWrapper = styled.button`
  ${({ theme }) => css`
    position: absolute;
    top: 0.5rem;
    right: 1.25rem;
    opacity: 0;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    border-radius: 0.25rem;
    padding: 0.25rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 150ms ease;
    color: ${theme.colors.foreground[40]};
    z-index: 1;

    &:hover {
      color: ${theme.colors.brand.primary.default};
      border-color: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const Container = styled.div<ContainerProps>`
  ${({
    theme,
    $removedMessage: isRemovedMessage,
    $currentUser: isCurrentUser,
  }) => css`
    display: flex;
    align-items: flex-start;
    opacity: ${isRemovedMessage ? 0.6 : 1};
    padding: 0.75rem 0.4rem;
    background: ${theme.colors.background[100]};
    position: relative;
    z-index: 0;

    > img {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.5rem;
    }

    &:hover ${ReplyButtonWrapper} {
      opacity: 1;
    }

    ${isCurrentUser &&
    css`
      flex-direction: row-reverse;

      > img {
        margin: 0 0 0 1rem;
      }
    `};
  `}
`;

export const MessageContent = styled.div<HasCurrentUserProps>`
  flex: 1;
  display: flex;
  flex-direction: column;

  > p {
    font-size: 0.75rem;
  }

  ${({ $currentUser: isCurrentUser }) =>
    isCurrentUser &&
    css`
      > p,
      div {
        margin-left: auto;
        text-align: right;
      }
    `};
`;

export const MessageInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.25rem;
  width: 100%;
  position: relative;
`;

export const MessageHeader = styled.div<HasCurrentUserProps>`
  ${({ theme, $currentUser: isCurrentUser }) => css`
    display: flex;
    justify-content: space-between;

    > div {
      display: flex;
      flex: 1;
      flex-direction: ${isCurrentUser ? 'row-reverse' : 'row'};
      align-items: center;
      gap: 1.5rem;

      > h4 {
        display: flex;
        flex-direction: ${isCurrentUser ? 'row-reverse' : 'row'};
        align-items: center;
        font-size: 0.875rem;
        gap: 0.75rem;

        > time {
          color: ${theme.colors.foreground[60]};
          display: inline-block;
          align-self: flex-end;
          font-size: 0.625rem;
        }
      }

      > span {
        display: flex;
        align-items: center;
        gap: 0.1875rem;
        font-size: 0.5125rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        background: ${theme.colors.brand.primary.default};
        color: ${theme.colors.base.text};
        padding: 0.125rem 0.625rem;
        border-radius: 0.375rem;
      }
    }
  `}
`;

export const ModeratorOptionsContainer = styled.div<HasCurrentUserProps>`
  ${({ $currentUser: isCurrentUser }) => css`
    position: absolute;
    top: -0.875rem;
    opacity: 0.15;
    transition: 180ms ease-in-out;

    :hover {
      opacity: 1;
    }

    .tippy-content {
      padding: 0;
    }

    ${isCurrentUser
      ? css`
          left: -1.5rem;
        `
      : css`
          right: -1.5rem;
        `}
  `}
`;

export const ModeratorOptionsButton = styled.button`
  ${({ theme }) => css`
    background: none;
    font-size: 0.75rem;
    white-space: nowrap;
    width: 100%;
    border-radius: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: none;
    cursor: pointer;
    justify-content: flex-start;
    transition: 180ms ease-in-out;

    :hover {
      background: ${theme.colors.brand.primary.default};
      color: ${theme.colors.base.text};
    }

    :first-child {
      border-radius: 0.25rem 0.25rem 0 0;
    }
    :last-child {
      border-radius: 0 0 0.25rem 0.25rem;
    }
  `}
`;

export const ReplyQuote = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: color-mix(
      in srgb,
      ${theme.colors.brand.primary.default} 4%,
      transparent
    );
    border: 1px solid
      color-mix(in srgb, ${theme.colors.brand.primary.default} 8%, transparent);
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background 0.1s;
    margin-bottom: 0.25rem;
    max-width: 90%;

    &:hover {
      background: color-mix(
        in srgb,
        ${theme.colors.brand.primary.default} 7%,
        transparent
      );
    }
  `}
`;

export const ReplyQuoteIcon = styled.span`
  ${({ theme }) => css`
    width: 0.875rem;
    height: 0.875rem;
    margin-top: 0.125rem;
    color: ${theme.colors.brand.primary.default};
    flex-shrink: 0;
    opacity: 0.5;
    display: flex;

    svg {
      width: 100%;
      height: 100%;
    }
  `}
`;

export const ReplyQuoteBody = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.15;
`;

export const ReplyQuoteAuthor = styled.span`
  ${({ theme }) => css`
    font-size: 0.65625rem;
    font-weight: 700;
    color: ${theme.colors.brand.primary.default};
    margin-right: 0.25rem;
  `}
`;

export const ReplyQuoteText = styled.span`
  font-size: 0.6875rem;
  font-weight: 400;
  color: #7a8ba7;
  word-break: break-word;
  line-height: 1.2;
`;
